/**
 * Truncate a numeric input string to at most `maxLength` digits before the
 * decimal point.
 *
 * The limit bounds the magnitude of the value, so only the integer part counts
 * toward it — a leading sign and everything after the decimal point are
 * preserved. Returns the value unchanged when `maxLength` is not a positive
 * number.
 *
 * @example
 * truncateNumber('20260', 4) => '2026'
 * truncateNumber('1234.5', 2) => '12.5'
 * truncateNumber('-12.345', 4) => '-12.345'
 * truncateNumber('123456') => '123456'
 */
export function truncateNumber(value, maxLength) {
    if (!value || !maxLength || maxLength <= 0)
        return value;
    const [integerPart, ...fractionalParts] = value.split('.');
    let digits = 0;
    const truncatedIntegerPart = Array.from(integerPart).reduce((result, char) => {
        const isDigit = char >= '0' && char <= '9';
        if (isDigit && digits >= maxLength)
            return result;
        if (isDigit)
            digits += 1;
        return result + char;
    }, '');
    return [truncatedIntegerPart, ...fractionalParts].join('.');
}
/**
 * Digit cap for a fully bounded number field.
 *
 * A cap is only meaningful when the value's magnitude is bounded on both sides,
 * so this returns `undefined` unless BOTH `min` and `max` are set — an open-ended
 * range (no `max`, or no `min` while negatives are allowed) must not be capped.
 * When both are set, the cap is the larger digit count of the two bounds by
 * magnitude (the sign is not counted, since `truncateNumber` ignores it) — so
 * `[-5000, 100]` yields 4, not 3.
 *
 * Counting every digit of a fractional bound rather than just its integer part
 * is deliberate: shrinking the cap would let `truncateNumber` rewrite an
 * out-of-range entry into a different in-range one before the max rule sees it
 * (`100` in a `[0, 99.9]` field becoming a silently valid `10`).
 *
 * @example
 * getNumberMaxLength(1900, 2026) => 4
 * getNumberMaxLength(-5000, 100) => 4
 * getNumberMaxLength(undefined, 500) => undefined // min open => could be -100000
 * getNumberMaxLength(0, undefined) => undefined   // max open => unbounded above
 */
export function getNumberMaxLength(min, max) {
    if (typeof min !== 'number' || typeof max !== 'number')
        return undefined;
    const digitCount = (value) => Math.abs(value).toString().replace(/\D/g, '').length;
    return Math.max(digitCount(min), digitCount(max));
}
